/**
 * Core astrology domain types shared by the calculation engine, the app UI,
 * the matching engine and Firebase Functions.
 *
 * Conventions:
 * - All longitudes are ecliptic longitudes in degrees [0, 360).
 * - `sign` indexes are 0-based from Aries (0) .. Pisces (11), in both the
 *   tropical and sidereal zodiacs.
 * - Months are 1-based (January = 1). All civil dates/times are LOCAL to the
 *   birthplace unless a field is explicitly suffixed `Utc`/`ISO`.
 */

export type ZodiacSystem = 'tropical' | 'sidereal';

/** How confident we are in the birth time. Drives the entire chart mode. */
export type TimeConfidence = 'exact' | 'approximate' | 'unknown';

export type HouseSystem = 'placidus' | 'whole-sign' | 'equal';
/**
 * Sidereal zodiac offsets. Lahiri is the Indian government standard and the
 * default; Raman and KP have substantial regional followings (KP especially
 * in Tamil Nadu and the South).
 */
export type AyanamsaId = 'lahiri' | 'raman' | 'kp';
export type NodeConvention = 'mean';

/** The three major Indian chart drawing styles. */
export type JyotishChartStyle = 'north' | 'south' | 'east';

export type Planet =
  | 'sun'
  | 'moon'
  | 'mercury'
  | 'venus'
  | 'mars'
  | 'jupiter'
  | 'saturn'
  | 'uranus'
  | 'neptune'
  | 'pluto'
  | 'rahu'
  | 'ketu';

/** The nine grahas of classical Jyotish. */
export const JYOTISH_PLANETS: Planet[] = [
  'sun',
  'moon',
  'mars',
  'mercury',
  'jupiter',
  'venus',
  'saturn',
  'rahu',
  'ketu',
];

/** Bodies used in the Western chart. */
export const WESTERN_PLANETS: Planet[] = [
  'sun',
  'moon',
  'mercury',
  'venus',
  'mars',
  'jupiter',
  'saturn',
  'uranus',
  'neptune',
  'pluto',
];

export const SIGN_NAMES = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
] as const;

export const RASHI_NAMES = [
  'Mesha',
  'Vrishabha',
  'Mithuna',
  'Karka',
  'Simha',
  'Kanya',
  'Tula',
  'Vrischika',
  'Dhanu',
  'Makara',
  'Kumbha',
  'Meena',
] as const;

/** The nine bodies Jyotish reads. `Planet` minus the outer three. */
export type GrahaBody =
  | 'sun'
  | 'moon'
  | 'mars'
  | 'mercury'
  | 'jupiter'
  | 'venus'
  | 'saturn'
  | 'rahu'
  | 'ketu';

/**
 * Sanskrit graha name per body id — the single source for UI copy, which is
 * Indian-first everywhere outside explicitly Western surfaces.
 *
 * NOTE: `@astrally/astro-engine` also exports a `GRAHA_NAMES` (a Partial over
 * all of `Planet`, spelling Mercury "Budha") that predates this contract
 * layer. Same rule as the horoscope types in index.ts: never import the same
 * name from both packages in one file. Outside the engine, prefer this one.
 */
export const GRAHA_NAMES = {
  sun: 'Surya',
  moon: 'Chandra',
  mars: 'Mangal',
  mercury: 'Budh',
  jupiter: 'Guru',
  venus: 'Shukra',
  saturn: 'Shani',
  rahu: 'Rahu',
  ketu: 'Ketu',
} as const satisfies Record<GrahaBody, string>;

/**
 * The twelve bhavas by their traditional names, 1-based like every bhava
 * count. `gist` is the life areas in one breath, ready for a subtitle.
 */
export interface BhavaName {
  /** 1-12 */
  n: number;
  name: string;
  gist: string;
}

export const BHAVA_NAMES: BhavaName[] = [
  { n: 1, name: 'Tanu', gist: 'self, body, first impressions' },
  { n: 2, name: 'Dhan', gist: 'money, possessions, speech, family' },
  { n: 3, name: 'Sahaj', gist: 'siblings, courage, skills, short trips' },
  { n: 4, name: 'Sukh', gist: 'home, mother, land, inner peace' },
  { n: 5, name: 'Putra', gist: 'children, creativity, intelligence, romance' },
  { n: 6, name: 'Ari', gist: 'health, daily work, debts, rivals' },
  { n: 7, name: 'Yuvati', gist: 'marriage, partnership, the other person' },
  { n: 8, name: 'Randhra', gist: 'shared assets, depth, sudden change' },
  { n: 9, name: 'Dharma', gist: 'belief, teachers, fortune, long journeys' },
  { n: 10, name: 'Karma', gist: 'career, action, public standing' },
  { n: 11, name: 'Labha', gist: 'gains, income, friends, networks' },
  { n: 12, name: 'Vyaya', gist: 'expenses, rest, retreat, release' },
];

export const NAKSHATRA_NAMES = [
  'Ashwini',
  'Bharani',
  'Krittika',
  'Rohini',
  'Mrigashira',
  'Ardra',
  'Punarvasu',
  'Pushya',
  'Ashlesha',
  'Magha',
  'Purva Phalguni',
  'Uttara Phalguni',
  'Hasta',
  'Chitra',
  'Swati',
  'Vishakha',
  'Anuradha',
  'Jyeshtha',
  'Mula',
  'Purva Ashadha',
  'Uttara Ashadha',
  'Shravana',
  'Dhanishta',
  'Shatabhisha',
  'Purva Bhadrapada',
  'Uttara Bhadrapada',
  'Revati',
] as const;

/** Vimshottari lords per nakshatra, repeating Ketu..Mercury three times. */
export const NAKSHATRA_LORDS: Planet[] = [
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury',
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury',
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury',
];

/** Sign rulers (classical rulerships, used for lordships in both systems). */
export const SIGN_LORDS: Planet[] = [
  'mars',    // Aries
  'venus',   // Taurus
  'mercury', // Gemini
  'moon',    // Cancer
  'sun',     // Leo
  'mercury', // Virgo
  'venus',   // Libra
  'mars',    // Scorpio
  'jupiter', // Sagittarius
  'saturn',  // Capricorn
  'saturn',  // Aquarius
  'jupiter', // Pisces
];

// ---------------------------------------------------------------------------
// Birth input
// ---------------------------------------------------------------------------

export interface GeoPlace {
  lat: number;
  lon: number;
  /** IANA timezone, e.g. "Asia/Kolkata". */
  timezone: string;
  label?: string;
  countryCode?: string;
}

export interface BirthDate {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
}

export interface ClockTime {
  /** 0-23 */
  hour: number;
  /** 0-59 */
  minute: number;
}

export interface TimeWindow {
  start: ClockTime;
  end: ClockTime;
}

export type BroadTimeLabel = 'morning' | 'afternoon' | 'evening' | 'night' | 'custom';

export type BirthTimeInput =
  | { kind: 'exact'; time: ClockTime }
  | { kind: 'approximate'; window: TimeWindow; label?: BroadTimeLabel }
  | { kind: 'unknown' };

export interface BirthInput {
  date: BirthDate;
  timeInput: BirthTimeInput;
  place: GeoPlace;
}

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export interface SignPosition {
  /** 0 = Aries .. 11 = Pisces */
  sign: number;
  signName: string;
  /** Degrees within the sign, [0, 30). */
  degInSign: number;
}

export interface NakshatraPosition {
  /** 0 = Ashwini .. 26 = Revati */
  index: number;
  name: string;
  pada: 1 | 2 | 3 | 4;
  lord: Planet;
}

export interface PlanetPosition {
  planet: Planet;
  /** Tropical ecliptic longitude, degrees [0,360). */
  lonTropical: number;
  /** Sidereal ecliptic longitude (tropical - ayanamsa), degrees [0,360). */
  lonSidereal: number;
  /** Ecliptic latitude, degrees. */
  latEcliptic: number;
  /** Longitudinal speed, degrees/day (negative = retrograde). */
  speed: number;
  retrograde: boolean;
  tropical: SignPosition;
  sidereal: SignPosition;
  /** From the sidereal longitude. */
  nakshatra: NakshatraPosition;
}

// ---------------------------------------------------------------------------
// Houses, angles, vargas
// ---------------------------------------------------------------------------

export interface HousesResult {
  system: HouseSystem;
  /**
   * 12 cusp longitudes in the tropical zodiac, index 0 = 1st house cusp.
   * For whole-sign, cusps are the starts of the signs.
   */
  cusps: number[];
  /** Set when Placidus is undefined at extreme latitudes and we fell back. */
  fallbackApplied?: boolean;
}

export interface AnglesResult {
  /** Tropical longitudes. */
  ascendant: number;
  mc: number;
  /** Sidereal equivalents. */
  ascendantSidereal: number;
  mcSidereal: number;
}

export type VargaId = 'D1' | 'D9' | 'D10';

export interface VargaPlacement {
  body: Planet | 'lagna';
  sign: number;
  signName: string;
  rashiName: string;
}

export interface VargaChart {
  varga: VargaId;
  description: string;
  placements: VargaPlacement[];
}

// ---------------------------------------------------------------------------
// Dashas
// ---------------------------------------------------------------------------

export interface DashaPeriod {
  lord: Planet;
  /** 1 = mahadasha, 2 = antardasha, 3 = pratyantardasha */
  level: 1 | 2 | 3;
  startISO: string;
  endISO: string;
  children?: DashaPeriod[];
}

export interface VimshottariResult {
  moonLonSidereal: number;
  moonNakshatra: NakshatraPosition;
  /** Years of the birth mahadasha remaining at birth. */
  balanceYears: number;
  /** Mahadashas (level 1) with antardashas (2) and pratyantardashas (3). */
  periods: DashaPeriod[];
  /** Convention used to convert dasha years to days (e.g. 365.25). */
  yearDays: number;
}

// ---------------------------------------------------------------------------
// Aspects
// ---------------------------------------------------------------------------

/**
 * Ptolemaic majors plus the minor aspects most Western astrologers expect.
 * The quincunx in particular is routinely read and its absence is noticed.
 */
export type WesternAspectType =
  | 'conjunction'
  | 'opposition'
  | 'trine'
  | 'square'
  | 'sextile'
  | 'quincunx'
  | 'semi-sextile'
  | 'semi-square'
  | 'sesquiquadrate';

export const MAJOR_ASPECTS: WesternAspectType[] = [
  'conjunction',
  'opposition',
  'trine',
  'square',
  'sextile',
];

/** Part of Fortune is a calculated point, not a body. */
export type ChartPointId = Planet | 'ascendant' | 'mc' | 'fortune';

export interface AspectHit {
  a: ChartPointId;
  b: ChartPointId;
  type: WesternAspectType;
  /** Deviation from exact, degrees. */
  orb: number;
}

export type AspectOrbConfig = Record<WesternAspectType, number> & {
  /** Extra orb allowed when Sun or Moon participates. */
  luminaryBonus: number;
};

/** Minors take tight orbs by convention — they are easy to over-detect. */
export const DEFAULT_ASPECT_ORBS: AspectOrbConfig = {
  conjunction: 8,
  opposition: 8,
  trine: 7,
  square: 7,
  sextile: 5,
  quincunx: 3,
  'semi-sextile': 2,
  'semi-square': 2,
  sesquiquadrate: 2,
  luminaryBonus: 2,
};

export interface DrishtiHit {
  from: Planet;
  to: Planet;
  /** House offset counted inclusively (7 = opposition aspect). */
  houseOffset: number;
  /** True for Mars 4/8, Jupiter 5/9, Saturn 3/10 special aspects. */
  special: boolean;
}

// ---------------------------------------------------------------------------
// Yogas / doshas / patterns
// ---------------------------------------------------------------------------

export interface YogaResult {
  id: string;
  name: string;
  kind: 'yoga' | 'dosha';
  present: boolean;
  /** Yogas needing the lagna are suppressed when birth time is not exact. */
  requiresLagna: boolean;
  summary: string;
  factors: string[];
}

export interface ChartPattern {
  type: 'stellium' | 'grand-trine' | 't-square' | 'grand-cross';
  points: ChartPointId[];
  detail: string;
}

// ---------------------------------------------------------------------------
// Transits
// ---------------------------------------------------------------------------

export interface TransitHit {
  transiting: Planet;
  natal: ChartPointId;
  type: WesternAspectType;
  orb: number;
}

export interface GocharPlacement {
  planet: Planet;
  sidereal: SignPosition;
  nakshatra: NakshatraPosition;
  /** House counted from natal Moon sign (1-12), when Moon sign is known. */
  houseFromMoon?: number;
  retrograde: boolean;
}

// ---------------------------------------------------------------------------
// Uncertainty (approximate / unknown birth-time modes)
// ---------------------------------------------------------------------------

export type FeatureStability = 'stable' | 'varying' | 'unavailable';

export interface FeaturePossibility {
  value: string;
  /** Local clock time span in which this value holds, "HH:MM". */
  fromLocal: string;
  toLocal: string;
  /** Fraction of the window occupied by this value, 0..1. */
  fraction: number;
}

export interface StableFeature {
  id: string;
  label: string;
  value: string;
  system: 'jyotish' | 'western' | 'both';
  detail?: string;
}

export interface VaryingFeature {
  id: string;
  label: string;
  system: 'jyotish' | 'western' | 'both';
  possibilities: FeaturePossibility[];
}

export interface UnavailableFeature {
  id: string;
  label: string;
  reason: string;
}

export interface DashaPossibility {
  mahadashaLord: Planet;
  nakshatraName: string;
  /** Fraction of the time window producing this mahadasha at birth. */
  fraction: number;
}

export interface UncertaintyReport {
  windowLocalStart: string;
  windowLocalEnd: string;
  stepMinutes: number;
  sampleCount: number;
  stable: StableFeature[];
  varying: VaryingFeature[];
  unavailable: UnavailableFeature[];
  /**
   * Representative positions from the window midpoint — for rendering the
   * slow planets that are stable across the window. Never present exact
   * Moon/lagna values from this array when they vary.
   */
  midpointPositions: PlanetPosition[];
  moonRangeSidereal: { start: number; end: number };
  dasha?: { possibilities: DashaPossibility[]; note: string };
}

// ---------------------------------------------------------------------------
// Chart result
// ---------------------------------------------------------------------------

export interface ChartMeta {
  engineVersion: string;
  calculatedAtISO: string;
  ephemerisAdapter: string;
  zodiac: { tropical: true; sidereal: true };
  ayanamsa: { id: AyanamsaId; valueDeg: number };
  nodeConvention: NodeConvention;
  houseSystem: HouseSystem;
  timeConfidence: TimeConfidence;
  dashaYearDays: number;
}

export interface JyotishData {
  /** Sidereal lagna — only when time is exact. */
  lagna?: SignPosition;
  lagnaNakshatra?: NakshatraPosition;
  /** Bhava (house) index 1-12 per planet, whole-sign from lagna. Exact time only. */
  bhavas?: Partial<Record<Planet, number>>;
  vargas: VargaChart[];
  vimshottari?: VimshottariResult;
  drishti: DrishtiHit[];
  yogas: YogaResult[];
}

export interface WesternData {
  angles?: AnglesResult;
  houses?: HousesResult;
  /** House index 1-12 per planet (from Placidus/whole-sign cusps). Exact time only. */
  housePlacements?: Partial<Record<Planet, number>>;
  /** Part of Fortune — needs an exact time, since it depends on the Ascendant. */
  fortune?: {
    lon: number;
    /** Day charts use Asc+Moon−Sun; night charts reverse the luminaries. */
    sect: 'day' | 'night';
    sign: SignPosition;
    house: number;
  };
  aspects: AspectHit[];
  patterns: ChartPattern[];
}

export interface ChartResult {
  meta: ChartMeta;
  birth: {
    date: BirthDate;
    timeInput: BirthTimeInput;
    place: GeoPlace;
    /** Only set for exact-time charts. */
    utcISO?: string;
  };
  /** Exact-time (or reference-time) positions. Absent in window modes. */
  positions?: PlanetPosition[];
  jyotish?: JyotishData;
  western?: WesternData;
  /** Present for approximate/unknown modes. */
  uncertainty?: UncertaintyReport;
  /** Set when this chart is an explicitly-labelled reference chart, never a natal chart. */
  reference?: { kind: 'sunrise' | 'noon'; note: string };
}

// ---------------------------------------------------------------------------
// Derived summary synced to the backend (privacy: no exact birth data)
// ---------------------------------------------------------------------------

export interface AstroSummary {
  timeConfidence: TimeConfidence;
  sunSiderealSign: number;
  sunTropicalSign: number;
  /** Single value when known; multiple possibilities in window modes. */
  moonSiderealSigns: number[];
  moonNakshatras: number[];
  /** Only present when birth time is exact. */
  lagnaSign?: number;
  mangalDosha: 'yes' | 'no' | 'unknown';
  /** Venus & Mars signs help western compatibility without exposing birth data. */
  venusTropicalSign: number;
  marsTropicalSign: number;
  engineVersion: string;
}

// ---------------------------------------------------------------------------
// Compatibility
// ---------------------------------------------------------------------------

export interface KootaScore {
  koota:
    | 'varna'
    | 'vashya'
    | 'tara'
    | 'yoni'
    | 'grahaMaitri'
    | 'gana'
    | 'bhakoot'
    | 'nadi';
  points: number;
  max: number;
  note: string;
}

export interface AshtakootaResult {
  total: number;
  max: 36;
  kootas: KootaScore[];
}

// ---------------------------------------------------------------------------
// Rectification
// ---------------------------------------------------------------------------

export type LifeEventType =
  | 'major-move'
  | 'first-job'
  | 'career-shift'
  | 'marriage'
  | 'relationship-start'
  | 'separation'
  | 'education-milestone'
  | 'sibling-event'
  | 'family-event'
  | 'accident-or-surgery'
  | 'business-launch';

export interface LifeEvent {
  type: LifeEventType;
  /** Approximate date is fine; month precision accepted. */
  dateISO: string;
  note?: string;
}

export interface RectificationWindow {
  startLocal: string;
  endLocal: string;
  score: number;
  explanation: string[];
}

export interface RectificationResult {
  windows: RectificationWindow[];
  /** Always communicate this as an estimate, never a discovered fact. */
  disclaimer: string;
  eventsUsed: number;
}
